import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import dataService from '../services/dataService';
import { toast } from 'react-toastify';
import {
    EyeIcon,
    ChatBubbleOvalLeftEllipsisIcon as CommentIcon,
    PencilSquareIcon,
    XMarkIcon,
    PaperAirplaneIcon,
    MagnifyingGlassIcon,
    TrashIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const ReferralsSent = () => {
    const { user } = useAuth();
    const activeRole = sessionStorage.getItem('activeRole');
    const isActingAsChapterAdmin = user?.role === 'chapter_admin' && activeRole !== 'member';
    const isOrgAdmin = user?.role === 'admin';
    const [referrals, setReferrals] = useState([]);
    const [members, setMembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Modal States
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [viewModal, setViewModal] = useState({ isOpen: false, data: null });
    const [commentModal, setCommentModal] = useState({ isOpen: false, data: null, comments: [], newComment: '' });
    const [editModal, setEditModal] = useState({ isOpen: false, data: null });
    const [sendModal, setSendModal] = useState({ isOpen: false });
    const commentInputRef = useRef(null);

    const handleReplyClick = (authorName) => {
        setCommentModal(prev => ({
            ...prev,
            newComment: `@${authorName} `
        }));
        setTimeout(() => {
            if (commentInputRef.current) {
                commentInputRef.current.focus();
            }
        }, 50);
    };

    // Form states for Send/Edit
    const [formData, setFormData] = useState({
        referredTo: '',
        referralName: '',
        contactNo: '',
        companyName: '',
        referralDescription: '',
        referralFlag: 'Select Flag',
        status: 'Open',
        businessDoneAmount: ''
    });

    useEffect(() => {
        fetchSettings();
        fetchReferrals();
        fetchMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateContactNo = (contactNo) => {
        const phone = contactNo.trim();

        // Must contain exactly 10 digits
        if (!/^\d{10}$/.test(phone)) {
            return 'Contact number must be exactly 10 digits.';
        }

        // Do not allow all zeros
        if (/^0{10}$/.test(phone)) {
            return 'Please enter a valid contact number. 0000000000 is not allowed.';
        }

        // // Optional: Indian mobile number should start from 6-9
        // if (!/^[6-9]\d{9}$/.test(phone)) {
        //     return 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.';
        // }

        return '';
    };

    const fetchSettings = async () => {
        try {
            const res = await dataService.getOrgSettings();
            if (res.data.success) {
                const orgSettings = typeof res.data.data.settings === 'string'
                    ? JSON.parse(res.data.data.settings || '{}')
                    : (res.data.data.settings || {});
                setSettings(orgSettings);
            }
        } catch (error) {
            console.error('Failed to load settings');
        }
    };

    const fetchReferrals = async () => {
        try {
            const res = await dataService.getSentReferrals();
            setReferrals(res.data.data);
        } catch (error) {
            toast.error('Failed to load referrals');
        }
    };

    const fetchMembers = async () => {
        try {
            const res = await dataService.getUsers({ context: 'referral' });
            if (res.data.success) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                let allMembers = res.data.data.filter(u => {
                    const isApproved = u.status === 'approved';
                    const isValidRole = u.role === 'member' || u.role === 'chapter_admin';
                    const isNotCurrent = u.id !== user?.id;
                    let isNotExpired = true;
                    if (u.membership_renewal_date) {
                        const renewalDate = new Date(u.membership_renewal_date);
                        isNotExpired = renewalDate >= today;
                    }
                    return isApproved && isValidRole && isNotCurrent && isNotExpired;
                });

                // Apply "Give Referral Within Chapter Members Only" setting (Frontend safety)
                if (settings?.referrals_across_chapter && user?.chapter) {
                    allMembers = allMembers.filter(m => m.chapter === user.chapter);
                }

                setMembers(allMembers);
            }
        } catch (error) {
            console.error("Failed to fetch members");
        }
    };

    // Refresh members when settings are loaded
    useEffect(() => {
        if (settings) {
            fetchMembers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    const handleSendReferral = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        const contactError = validateContactNo(formData.contactNo);

        if (contactError) {
            toast.error(contactError);
            return;
        }
        setIsSubmitting(true);
        console.log("Submitting referral...", formData);
        try {
            const payload = {
                referenceName: formData.referralName, // Mapping to backend field
                refOrganizationName: formData.companyName,
                contactEmail: '',
                contactPhone: formData.contactNo,
                description: formData.referralDescription,
                referralFlag: formData.referralFlag === 'Select Flag' ? '' : formData.referralFlag,
                referredTo: formData.referredTo
            };
            console.log("Payload:", payload);
            await dataService.createReference(payload);
            toast.success('Referral sent successfully');
            setSendModal({ isOpen: false });
            resetForm();
            fetchReferrals();
        } catch (error) {
            console.error("Submission error:", error);
            toast.error('Failed to send referral');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditReferral = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const payload = {
                reference_name: formData.referralName,
                ref_organization_name: formData.companyName,
                contact_email: '',
                contact_phone: formData.contactNo,
                description: formData.referralDescription,
                status: formData.status,
                referral_flag: formData.referralFlag,
                referred_to: formData.referredTo,
                business_done_amount: formData.businessDoneAmount
            };
            await dataService.updateReference(editModal.data.id, payload);
            toast.success('Referral updated successfully');
            setEditModal({ isOpen: false, data: null });
            fetchReferrals();
        } catch (error) {
            console.error("Update error:", error);
            const msg = error.response?.data?.message || 'Failed to update referral';
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchComments = async (referralId) => {
        try {
            const res = await dataService.getReferralComments(referralId);
            setCommentModal(prev => ({ ...prev, comments: res.data.data }));
        } catch (error) {
            console.error('Failed to load comments');
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        const activeRef = commentModal.data;
        if (!activeRef) return;
        if (!commentModal.newComment.trim()) return;
        try {
            await dataService.addReferralComment(activeRef.id, commentModal.newComment);
            setCommentModal(prev => ({ ...prev, newComment: '' }));
            fetchComments(activeRef.id);
        } catch (error) {
            toast.error('Failed to add comment');
        }
    };

    const resetForm = () => {
        setFormData({
            referredTo: '',
            referralName: '',
            contactNo: '',
            companyName: '',
            referralDescription: '',
            referralFlag: 'Select Flag',
            status: 'Open',
            businessDoneAmount: ''
        });
    };

    const openEdit = (ref) => {
        setFormData({
            referredTo: ref.referred_to || '',
            referralName: ref.reference_name || '',
            contactNo: ref.contact_phone || '',
            companyName: ref.ref_organization_name || '',
            referralDescription: ref.description || '',
            referralFlag: ref.referral_flag || 'Select Flag',
            status: ref.status || 'Open',
            businessDoneAmount: ref.business_done_amount || ''
        });
        setEditModal({ isOpen: true, data: ref });
    };

    const canEdit = (ref) => {
        const isCreator = Number(ref.user_id) === Number(user?.id);
        return isCreator;
    };

    const canDelete = (ref) => {
        const isCreator = Number(ref.user_id) === Number(user?.id);
        return isCreator;
    };

    const handleDeleteReferral = async (id) => {
        if (window.confirm("Are you sure you want to delete this referral?")) {
            try {
                await dataService.deleteReference(id);
                toast.success('Referral deleted successfully');
                fetchReferrals();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to delete referral');
            }
        }
    };

    const filteredReferrals = referrals.filter(ref => {
        const matchesSearch = String(ref.reference_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(ref.recipient_name || ref.referred_to || '').toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filteredReferrals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedReferrals = filteredReferrals.slice(startIndex, endIndex);

    return (
        <div className="space-y-6 h-full flex flex-col animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight italic">Referral Send</h1>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mt-1">Track and manage business opportunities you've shared</p>
                </div>
                {!isActingAsChapterAdmin && (
                    <button
                        onClick={() => { resetForm(); setSendModal({ isOpen: true }); }}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-2xl shadow-gray-200 hover:bg-[#4eb7f5] transition-all active:scale-95"
                    >
                        <PaperAirplaneIcon className="w-5 h-5 shadow-sm shadow-white/20" />
                        Send a Referral
                    </button>
                )}
            </div>

            {/* Premium Controls */}
            <div className="bg-white p-6 rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="relative flex-1 max-w-xl">
                    <MagnifyingGlassIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name, organization or referred member..."
                        className="w-full bg-gray-50 border border-transparent rounded-2xl pl-12 pr-12 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-gray-100 focus:bg-white focus:border-gray-200 transition-all shadow-inner"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="flex gap-4">
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 px-4 py-2 rounded-xl border border-gray-100 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        Total Sent: {referrals.length}
                    </div>
                </div>
            </div>

            {/* Premium Table Content */}
            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden flex-1 flex flex-col">
                {filteredReferrals.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-400">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                            <PaperAirplaneIcon className="w-10 h-10 opacity-20" />
                        </div>
                        <p className="text-xl font-black uppercase tracking-widest opacity-30 italic px-8 text-center">No referrals sent yet</p>
                    </div>
                ) : (
                    <>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {paginatedReferrals.map((ref) => (
                                    <div
                                        key={ref.id}
                                        className="group relative bg-white border-2 border-slate-100 rounded-[2rem] p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                                    >
                                        <div>
                                            {/* Header with Flow */}
                                            <div className="flex items-center justify-between gap-3 mb-6">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    {isActingAsChapterAdmin ? (
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">From</span>
                                                                <span className="text-sm font-black text-slate-900 truncate">{ref.sender_name}</span>
                                                                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider truncate">{ref.sender_chapter || 'N/A'}</span>
                                                            </div>
                                                            <ArrowRightIcon className="w-4 h-4 text-slate-300 shrink-0 mx-1 animate-pulse" />
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">To</span>
                                                                <span className="text-sm font-black text-slate-900 truncate">{ref.recipient_name || ref.referred_to}</span>
                                                                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider truncate">{ref.recipient_chapter || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Referred To</span>
                                                            <span className="text-sm font-black text-slate-900 truncate">{ref.recipient_name || ref.referred_to}</span>
                                                            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider truncate">{ref.recipient_chapter || 'N/A'}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="p-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-slate-400 shrink-0">
                                                    <PaperAirplaneIcon className="w-5 h-5" />
                                                </div>
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 mb-6">
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Date Sent
                                                    </p>
                                                    <span className="text-xs font-bold text-slate-700">
                                                        {new Date(ref.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Priority
                                                    </p>
                                                    <span className={clsx(
                                                        "inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider border",
                                                        ref.referral_flag === 'Hot' ? "bg-red-50 text-red-700 border-red-100" :
                                                            ref.referral_flag === 'Warm' ? "bg-amber-50 text-amber-700 border-amber-100" :
                                                                "bg-blue-50 text-blue-700 border-blue-100"
                                                    )}>
                                                        {ref.referral_flag || 'Medium'}
                                                    </span>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Status
                                                    </p>
                                                    <span className={clsx(
                                                        "inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all",
                                                        ref.status === 'Business Done' ? "bg-emerald-50 text-emerald-700 border-emerald-100 shadow-sm" :
                                                            ref.status === 'Closed' ? "bg-gray-50 text-gray-500 border-gray-100" :
                                                                "bg-blue-50 text-blue-700 border-blue-100 shadow-sm shadow-blue-50"
                                                    )}>
                                                        <div className={clsx("w-1.5 h-1.5 rounded-full",
                                                            ref.status === 'Business Done' ? "bg-emerald-500" :
                                                                ref.status === 'Closed' ? "bg-gray-400" : "bg-blue-500"
                                                        )} />
                                                        {ref.status || 'Open'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions Footer */}
                                        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-50">
                                            {/* View Details */}
                                            {!(isActingAsChapterAdmin && settings?.chapter_referral_thirdparty_data === true) && (
                                                <button
                                                    onClick={() => {
                                                        setViewModal({ isOpen: true, data: ref });
                                                        setCommentModal({ isOpen: false, data: ref, comments: [], newComment: '' });
                                                        fetchComments(ref.id);
                                                    }}
                                                    className="p-2.5 bg-white border border-gray-200 text-gray-400 rounded-xl hover:bg-slate-900 hover:text-white hover:border-gray-900 transition-all shadow-sm active:scale-95"
                                                    title="View Details"
                                                >
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                            )}

                                            {/* View Comments */}
                                            {!isOrgAdmin && !isActingAsChapterAdmin && (
                                                <button
                                                    onClick={() => {
                                                        setCommentModal({ isOpen: true, data: ref, comments: [], newComment: '' });
                                                        setViewModal({ isOpen: false, data: null });
                                                        fetchComments(ref.id);
                                                    }}
                                                    className="p-2.5 bg-white border border-gray-200 text-gray-400 rounded-xl hover:text-[#4eb7f5] hover:bg-blue-50 hover:border-[#4eb7f5] transition-all shadow-sm active:scale-95"
                                                    title="View Comments"
                                                >
                                                    <CommentIcon className="w-4 h-4" />
                                                </button>
                                            )}

                                            {/* Edit Referral */}
                                            {ref.status !== 'Business Done' && canEdit(ref) && (
                                                <button
                                                    onClick={() => openEdit(ref)}
                                                    className="p-2.5 bg-white border border-gray-200 text-gray-400 rounded-xl hover:text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 transition-all shadow-sm active:scale-95"
                                                    title="Edit Referral"
                                                >
                                                    <PencilSquareIcon className="w-4 h-4" />
                                                </button>
                                            )}

                                            {/* Delete Referral */}
                                            {ref.status !== 'Business Done' && canDelete(ref) && (
                                                <button
                                                    onClick={() => handleDeleteReferral(ref.id)}
                                                    className="p-2.5 bg-white border border-gray-200 text-gray-400 rounded-xl hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-all shadow-sm active:scale-95"
                                                    title="Delete Referral"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Pagination Controls */}
                        <div className="flex flex-col md:flex-row items-center justify-between px-8 py-5 border-t border-slate-100 gap-4 bg-gray-50/50">
                            <div className="text-sm font-semibold text-slate-600">
                                Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredReferrals.length)}</span> of{' '}
                                <span className="font-black text-slate-900">{filteredReferrals.length}</span> {filteredReferrals.length === 1 ? 'referral' : 'referrals'}
                            </div>

                            {filteredReferrals.length > itemsPerPage && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                            : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                            }`}
                                    >
                                        Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {[...Array(totalPages)].map((_, index) => {
                                            const pageNum = index + 1;
                                            if (
                                                pageNum === 1 ||
                                                pageNum === totalPages ||
                                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                            ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                            : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                            : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                            }`}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Inline sections removed - now using modals below */}

            {/* Modals for Edit, Send, View and Comments (Centered pop-ups) */}
            {(editModal.isOpen || sendModal.isOpen || viewModal.isOpen || commentModal.isOpen) && (
                <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className={clsx(
                        "bg-white rounded-[2.5rem] shadow-2xl w-full max-h-[90vh] overflow-y-auto relative border border-gray-100",
                        viewModal.isOpen ? "max-w-4xl" : "max-w-2xl"
                    )}>
                        {/* Global Close Button */}
                        <button
                            onClick={() => {
                                setEditModal({ isOpen: false, data: null });
                                setSendModal({ isOpen: false });
                                setViewModal({ isOpen: false, data: null });
                                setCommentModal({ isOpen: false, data: null, comments: [], newComment: '' });
                            }}
                            className={clsx(
                                "absolute top-6 right-8 z-10 p-2 rounded-2xl bg-gray-50 text-gray-400 hover:text-black hover:bg-gray-100 transition-all active:scale-90 shadow-sm",
                                (viewModal.isOpen || commentModal.isOpen) ? "text-white/80 hover:text-white hover:bg-white/10" : ""
                            )}
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>

                        {/* View Content */}
                        {viewModal.isOpen && (
                            <div className="animate-in zoom-in-95 duration-400">
                                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-10 py-8 mb-4">
                                    <div className="flex items-center gap-4 text-white">
                                        <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md border border-white/20">
                                            <EyeIcon className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h2 className="text-3xl font-black italic tracking-tight">Referral Details & Timeline</h2>
                                            <p className="text-[11px] font-bold text-white/70 uppercase tracking-widest">Business opportunity overview & discussion</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-10">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Left Column: Details */}
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block ml-1">Client Name</label>
                                                    <p className="text-sm font-black text-gray-900 bg-gray-50 px-5 py-3 rounded-xl border border-gray-100">{viewModal.data?.reference_name}</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block ml-1">Company Name</label>
                                                    <p className="text-sm font-black text-gray-900 bg-gray-50 px-5 py-3 rounded-xl border border-gray-100">{viewModal.data?.ref_organization_name || 'N/A'}</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Contact Number</label>
                                                    <p className="text-[13px] font-bold text-gray-800 px-1 ml-1">{viewModal.data?.contact_phone || 'Not provided'}</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Source Member</label>
                                                    <p className="text-[13px] font-black text-blue-600 px-1 ml-1 italic">{viewModal.data?.sender_name} ({viewModal.data?.sender_chapter || 'N/A'})</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Date Sent</label>
                                                    <p className="text-[13px] font-bold text-gray-800 px-1 ml-1">{new Date(viewModal.data?.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Referred To</label>
                                                    <p className="text-[13px] font-black text-emerald-600 px-1 ml-1 italic">{viewModal.data?.recipient_name || viewModal.data?.referred_to || 'N/A'} ({viewModal.data?.recipient_chapter || 'N/A'})</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block ml-1">Priority</label>
                                                    <p className="text-sm font-black text-gray-900 bg-gray-50 px-5 py-3 rounded-xl border border-gray-100">{viewModal.data?.referral_flag || 'Medium'}</p>
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Status</label>
                                                    <div className="pt-1">
                                                        <span className={clsx(
                                                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all",
                                                            viewModal.data?.status === 'Business Done' ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                                                                "bg-blue-50 text-blue-700 border-blue-100"
                                                        )}>
                                                            <div className={clsx("w-1.5 h-1.5 rounded-full", viewModal.data?.status === 'Business Done' ? "bg-emerald-500" : "bg-blue-500")} />
                                                            {viewModal.data?.status || 'Open'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-2.5">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Description / Notes</label>
                                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 italic relative">
                                                    <p className="text-[13px] font-medium text-gray-700 leading-relaxed px-2">
                                                        {viewModal.data?.description || 'No notes provided.'}
                                                    </p>
                                                </div>
                                            </div>

                                            {viewModal.data?.status === 'Business Done' && (
                                                <div className="p-4 bg-emerald-50 rounded-xl border-2 border-emerald-100 flex items-center justify-between">
                                                    <div>
                                                        <label className="text-[10px] font-black text-emerald-600 uppercase tracking-widest block ml-1">Revenue Generated</label>
                                                        <div className="flex items-center gap-1.5 text-xl font-black text-emerald-900 mt-1">
                                                            <span className="text-lg">₹</span>
                                                            {parseFloat(viewModal.data.business_done_amount || 0).toLocaleString('en-IN')}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right Column: Comments/Timeline */}
                                        <div className="border-t lg:border-t-0 lg:border-l border-gray-100 pt-6 lg:pt-0 lg:pl-8 flex flex-col h-full justify-between">
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <CommentIcon className="w-4.5 h-4.5 text-indigo-500" />
                                                    <h3 className="text-sm font-black text-gray-900 tracking-tight uppercase">Comments Timeline</h3>
                                                </div>

                                                <div className="min-h-[200px] max-h-[300px] overflow-y-auto mb-4 pr-2 flex flex-col gap-4 scrollbar-thin scrollbar-thumb-gray-200">
                                                    {commentModal.comments.length === 0 ? (
                                                        <div className="flex flex-1 flex-col items-center justify-center py-10 text-gray-400">
                                                            <p className="text-xs font-black uppercase tracking-widest opacity-20 italic">No activity recorded yet</p>
                                                        </div>
                                                    ) : (
                                                        commentModal.comments.map(c => (
                                                            <div key={c.id} className={clsx("max-w-[85%] rounded-[1.5rem] p-4 shadow-sm relative transition-all", c.user_id === user.id ? "ml-auto bg-blue-50 text-blue-900 border border-blue-100" : "mr-auto bg-gray-50 text-gray-800 border border-gray-100")}>
                                                                <div className="flex justify-between items-center mb-1.5 gap-6">
                                                                    <p className="text-[9px] font-black uppercase tracking-widest text-blue-600">{c.author_name}</p>
                                                                    <p className="text-[9px] text-gray-400 font-bold">{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                                </div>
                                                                <p className="text-xs leading-relaxed font-bold">{c.comment}</p>
                                                                {c.user_id !== user.id && (
                                                                    <div className="flex justify-start mt-1">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleReplyClick(c.author_name)}
                                                                            className="text-[9px] font-bold text-gray-500 hover:text-blue-600 transition-colors"
                                                                        >
                                                                            Reply
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>

                                                <form onSubmit={handleAddComment} className="flex gap-4 items-center">
                                                    <input
                                                        ref={commentInputRef}
                                                        type="text"
                                                        value={commentModal.newComment}
                                                        onChange={(e) => setCommentModal(prev => ({ ...prev, newComment: e.target.value }))}
                                                        placeholder="Add a comment or reply..."
                                                        className="flex-1 bg-gray-50 rounded-xl border-transparent border-0 px-4 py-3 text-sm focus:ring-4 focus:ring-blue-50 focus:bg-white transition-all shadow-inner"
                                                    />
                                                    <button
                                                        type="submit"
                                                        disabled={!commentModal.newComment.trim()}
                                                        className="bg-gray-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 disabled:opacity-20 transition-all shadow-lg active:scale-95"
                                                    >
                                                        Post
                                                    </button>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end pt-8 mt-6 border-t border-gray-100">
                                        <button onClick={() => setViewModal({ isOpen: false, data: null })} className="px-10 py-4 bg-gray-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all hover:bg-gray-800 active:scale-95 shadow-lg">Close View</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Comment Content */}
                        {commentModal.isOpen && (
                            <div className="animate-in zoom-in-95 duration-400">
                                <div className="bg-gradient-to-r from-[#4eb7f5] to-[#3da6e4] px-10 py-8 mb-4">
                                    <div className="flex items-center gap-4 text-white">
                                        <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md border border-white/20">
                                            <CommentIcon className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h2 className="text-3xl font-black italic tracking-tight">Timeline</h2>
                                            <p className="text-[11px] font-bold text-white/70 uppercase tracking-widest">Discussion & updates log</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-10">
                                    <div className="min-h-[300px] max-h-[500px] overflow-y-auto mb-10 pr-4 flex flex-col gap-6 scrollbar-thin scrollbar-thumb-gray-200">
                                        {commentModal.comments.length === 0 ? (
                                            <div className="flex flex-1 flex-col items-center justify-center py-20 text-gray-400">
                                                <p className="text-sm font-black uppercase tracking-widest opacity-20 italic">No activity recorded yet</p>
                                            </div>
                                        ) : (
                                            commentModal.comments.map(c => (
                                                <div key={c.id} className={clsx("max-w-[85%] rounded-[1.5rem] p-5 shadow-sm relative transition-all", c.user_id === user.id ? "ml-auto bg-blue-50 text-blue-900 border border-blue-100" : "mr-auto bg-gray-50 text-gray-800 border border-gray-100")}>
                                                    <div className="flex justify-between items-center mb-2 gap-8">
                                                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">{c.author_name}</p>
                                                        <p className="text-[10px] text-gray-400 font-bold">{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                    </div>
                                                    <p className="text-sm leading-relaxed font-bold">{c.comment}</p>
                                                    {c.user_id !== user.id && (
                                                        <div className="flex justify-start mt-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleReplyClick(c.author_name)}
                                                                className="text-[9px] font-bold text-gray-500 hover:text-blue-600 transition-colors"
                                                            >
                                                                Reply
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <form onSubmit={handleAddComment} className="flex gap-4 items-center">
                                        <input
                                            ref={commentInputRef}
                                            type="text"
                                            value={commentModal.newComment}
                                            onChange={(e) => setCommentModal(prev => ({ ...prev, newComment: e.target.value }))}
                                            placeholder="Add a comment or reply..."
                                            className="flex-1 bg-gray-50 rounded-2xl border-transparent border-0 px-6 py-4 text-sm focus:ring-4 focus:ring-blue-50 focus:bg-white transition-all shadow-inner"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!commentModal.newComment.trim()}
                                            className="bg-gray-900 text-white px-8 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-600 disabled:opacity-20 transition-all shadow-lg active:scale-95"
                                        >
                                            Post
                                        </button>
                                    </form>

                                    <div className="mt-8 pt-8 border-t border-gray-100 flex justify-end">
                                        <button
                                            onClick={() => setCommentModal({ isOpen: false, data: null, comments: [], newComment: '' })}
                                            className="px-10 py-4 bg-gray-100 text-gray-400 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-200 transition-all"
                                        >
                                            Close Timeline
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Edit/Send Forms */}
                        {(editModal.isOpen || sendModal.isOpen) && (
                            <div className="p-10 animate-in zoom-in-95 duration-400">
                                <div className="mb-10">
                                    <h2 className="text-3xl font-black text-gray-900 tracking-tight italic">
                                        {editModal.isOpen ? 'Edit Referral' : 'Send Referral'}
                                    </h2>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                                        {editModal.isOpen ? 'Modify shared opportunity details' : 'Share a new business lead'}
                                    </p>
                                </div>

                                <form onSubmit={editModal.isOpen ? handleEditReferral : handleSendReferral} className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Referred To Member</label>
                                            <select
                                                required
                                                disabled={editModal.isOpen}
                                                value={formData.referredTo}
                                                onChange={(e) => setFormData({ ...formData, referredTo: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            >
                                                <option value="">Select a member</option>
                                                {members.map(m => (
                                                    <option key={m.id} value={m.id}>{m.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Client Full Name</label>
                                            <input
                                                required
                                                type="text"
                                                placeholder="e.g. John Doe"
                                                value={formData.referralName}
                                                onChange={(e) => setFormData({ ...formData, referralName: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Contact Number</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. +91 99999 00000"
                                                value={formData.contactNo}
                                                onChange={(e) => setFormData({ ...formData, contactNo: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Priority Level</label>
                                            <select
                                                value={formData.referralFlag}
                                                onChange={(e) => setFormData({ ...formData, referralFlag: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            >
                                                <option value="Select Flag">Select Level</option>
                                                <option value="Hot">Hot (High)</option>
                                                <option value="Warm">Warm (Medium)</option>
                                                <option value="Cold">Cold (Low)</option>
                                            </select>
                                        </div>
                                        <div className="col-span-1 md:col-span-2 space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Company / Organization</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Acme Corp"
                                                value={formData.companyName}
                                                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                                className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="col-span-1 md:col-span-2 space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Additional Notes</label>
                                            <textarea
                                                rows="3"
                                                placeholder="Provide any context to help the receiver..."
                                                value={formData.referralDescription}
                                                onChange={(e) => setFormData({ ...formData, referralDescription: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-sm resize-none h-32"
                                            />
                                        </div>
                                        {editModal.isOpen && (
                                            <div className="col-span-1 md:col-span-2 space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Current Status</label>
                                                <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-6 py-5 border border-gray-100 italic">
                                                    <span className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-100">
                                                        {formData.status || 'Open'}
                                                    </span>
                                                    <p className="text-[11px] font-bold text-gray-400">Status updates are restricted to the receiver only.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4 pt-6">
                                        <button
                                            type="button"
                                            onClick={() => { setEditModal({ isOpen: false, data: null }); setSendModal({ isOpen: false }); }}
                                            className="flex-1 px-8 py-5 border-2 border-gray-100 text-gray-500 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-[2] px-8 py-5 bg-gray-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-2xl shadow-gray-300 hover:bg-[#4eb7f5] hover:shadow-[#4eb7f5]/20 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                                        >
                                            {isSubmitting ? 'Submitting...' : (editModal.isOpen ? 'Update Details' : 'Send Referral')}
                                            <PaperAirplaneIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReferralsSent;
